<script lang="ts">
  import LL from '$i18n/i18n-svelte';
  import { idToItem } from '$lib/er/item/stores';
  import { createTooltip, melt } from '@melt-ui/svelte';
  import ItemOptions from './item-options.svelte';
  import ItemBuildTree from './item-build-tree.svelte';
  import RawItemImage from './raw-item-image.svelte';
  import type { ComponentProps } from 'svelte';
  import { getItemGradeName, getItemName, getItemSubcategoryName } from './translation';
  import ItemSkills from './item-skills.svelte';

  export let id: number;
  export let size: ComponentProps<RawItemImage>['size'] = 5;
  export let goal = false;
  export let required = false;
  export let drone = false;
  export let square = false;

  const {
    elements: { trigger, content },
    states: { open },
  } = createTooltip({
    positioning: {
      placement: 'top',
    },
    openDelay: 200,
    closeOnPointerDown: false,
    disableHoverableContent: true,
    // forceVisible: true,
  });

  $: grade = $idToItem.get(id)?.gradeId ?? 'C';
</script>

<div
  use:melt={$trigger}
  class="item-image"
  data-grade={grade}
  data-size={size}
  data-square={square}
  data-goal={goal}
  data-drone={drone}
  data-required={required}
>
  <RawItemImage {id} {size} {square} />
  {#if $$slots.count}
    <span class="count">
      <slot name="count" />
    </span>
  {/if}
</div>
{#if $open}
  <div class="tooltip-content" use:melt={$content}>
    <div class="image-container" data-grade={grade}>
      <img alt={getItemName($LL, id)} src="/images/items/{id}.webp" height={36} />
    </div>
    <div class="content">
      <h2>{getItemName($LL, id)}</h2>
      <span data-grade={grade}
        >{getItemGradeName($LL, grade)} {getItemSubcategoryName($LL, id)}</span
      >
      <div class="options">
        <ItemOptions {id} />
      </div>
      <div class="skills">
        <ItemSkills {id} />
      </div>
      <ItemBuildTree {id} noTooltip />
    </div>
  </div>
{/if}

<style lang="postcss">
  .item-image {
    border-radius: var(--radius-1);
    aspect-ratio: 5 / 3;

    &[data-goal='true'] {
      &::before {
        background: url($lib/assets/images/flash.svg);
        position: absolute;
        content: '';
        left: -5px;
        top: -5px;
        display: block;
        width: 1.4rem;
        height: 1.4rem;
        filter: drop-shadow(0 2px 3px black);
      }
    }
    &[data-required='true'] {
      &::before {
        background: url($lib/assets/images/required.svg);
        position: absolute;
        content: '';
        left: 0px;
        top: 0px;
        display: block;
        width: 1rem;
        height: calc(1.4 / 1.1 * 100%);
        background-repeat: no-repeat;
        background-size: contain;
      }
    }
    &[data-drone='true'] {
      &::after {
        position: absolute;
        content: 'D';
        background-color: black;
        color: white;
        right: 0px;
        top: 0px;
        display: block;
        padding: 0 var(--space-2);
        font-size: var(--size-3);
        border-radius: var(--radius-1);
        background-repeat: no-repeat;
        background-size: contain;
      }
    }
  }

  img {
    object-fit: contain;
  }

  .count {
    padding: var(--space-1) var(--space-2);
    background-color: var(--black-a11);
    color: white;
    position: absolute;
    right: 0;
    bottom: 0;
    z-index: 1;
    border-radius: var(--radius-1);
    font-size: var(--size-3);
  }

  .tooltip-content {
    min-width: 20rem;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    background-color: var(--background);
    border-radius: var(--radius-2);
    box-shadow: 0 0.5rem 2rem 0.4rem var(--shadow-2);
    font-size: var(--size-5);
    z-index: 100;

    .image-container {
      display: flex;
      width: 100%;
      height: 5.2rem;
      align-items: center;
      padding-left: var(--space-5);

      &[data-grade='C'] {
        background: var(--gradient-item-common-dimmed);
      }

      &[data-grade='U'] {
        background: var(--gradient-item-uncommon-dimmed);
      }

      &[data-grade='R'] {
        background: var(--gradient-item-rare-dimmed);
      }

      &[data-grade='E'] {
        background: var(--gradient-item-epic-dimmed);
      }

      &[data-grade='L'] {
        background: var(--gradient-item-legendary-dimmed);
      }

      &[data-grade='M'] {
        background: var(--gradient-item-mythic-dimmed);
      }
    }

    .content {
      display: flex;
      flex-direction: column;
      margin: var(--space-5);
    }

    .options {
      display: flex;
      flex-direction: column;
      margin: var(--space-3) 0;
    }

    .skills {
      display: flex;
      flex-direction: column;
      gap: var(--space-2);
      max-width: 50ch;

      :global(h3) {
        font-size: var(--size-5);
      }
    }

    h2 {
      font-size: var(--size-6);
    }
  }
</style>
